import { theme, useResponsive } from "@product/scmp-sdk";
import { usePrevious, useSyncedRef } from "@react-hookz/web";
import { useHover, useInterval } from "ahooks";
import { useAtomValue } from "jotai";
import type { RefObject } from "react";
import { useCallback, useEffect, useState } from "react";
import { useIdle } from "react-use";

import type { AdUnit } from "~/components/advertisement/ad-slots/types";
import { isNativeAdUnit } from "~/components/advertisement/ad-slots/types";
import * as gpt from "~/components/advertisement/google-publisher-tag/hooks";
import { appAtom } from "~/lib/app/atoms";
import { useParsedCookie } from "~/lib/hooks";

export type AutoRefreshParameters = {
  adUnit: AdUnit;
  intersection: IntersectionObserverEntry | null;
  manualRefreshCount: number;
  options?: {
    disableIntervalRefresh?: boolean;
    disableScrollBackRefresh?: boolean;
  };
  reference: RefObject<HTMLElement>;
  slot: googletag.Slot | null;
};
export const useAutoRefresh = ({
  adUnit,
  intersection,
  manualRefreshCount,
  options,
  reference,
  slot,
}: AutoRefreshParameters) => {
  const shouldEnableAutoRefresh = useShouldEnableAutoRefresh(adUnit, slot);

  const isHovering = useHover(shouldEnableAutoRefresh ? reference : undefined);

  const { scrollBackRefreshCount } = useScrollBackRefresh(
    slot,
    intersection,
    !options?.disableScrollBackRefresh && shouldEnableAutoRefresh,
  );
  const { intervalRefreshCount } = useIntervalRefresh(
    slot,
    intersection,
    isHovering,
    !options?.disableIntervalRefresh && shouldEnableAutoRefresh,
  );

  // Cspell:ignore adrefresh adrefresh_scrollback
  const totalRefreshCount = manualRefreshCount + intervalRefreshCount + scrollBackRefreshCount;

  if (shouldEnableAutoRefresh) {
    slot?.updateTargetingFromMap({
      adrefresh: totalRefreshCount.toString(),
      adrefresh_event: manualRefreshCount.toString(),
      adrefresh_interval: intervalRefreshCount.toString(),
      adrefresh_scrollback: scrollBackRefreshCount.toString(),
    });
  }

  return { totalRefreshCount };
};

function useShouldEnableAutoRefresh(adUnit: string, slot: googletag.Slot | null) {
  const { advertisementConfig, ipGeoLocation } = useAtomValue(appAtom);
  const renderedInfo = gpt.useRenderSlotInfo(slot);

  const isNativeAd = isNativeAdUnit(adUnit);

  // To make testing easier since development environment need to be access in internal network thus can't use vpn
  const { value: shouldForceAllowAdSlotAutoRefreshCountry } = useParsedCookie(
    "shouldForceAllowAdSlotAutoRefreshCountry",
    raw => raw === "true",
    {
      initializeWithValue: true,
    },
  );
  const isDisabledCountry =
    !shouldForceAllowAdSlotAutoRefreshCountry &&
    advertisementConfig.autoRefresh.disabledCountries.includes(
      ipGeoLocation?.result?.country ?? "",
    );

  const isDisabledLineItem =
    renderedInfo?.lineItemId &&
    advertisementConfig.autoRefresh.disabledDirectCampaignLineItemIds.includes(
      renderedInfo.lineItemId,
    );

  const shouldEnableAutoRefresh = !isNativeAd && !isDisabledCountry && !isDisabledLineItem;

  return shouldEnableAutoRefresh;
}

function useIntervalRefresh(
  slot: googletag.Slot | null,
  intersection: IntersectionObserverEntry | null,
  isHovering: boolean,
  isEnable: boolean,
) {
  const { advertisementConfig } = useAtomValue(appAtom);
  const refreshIntervalMs = advertisementConfig.autoRefresh.refreshIntervalMs;
  const delayIntervalMs = 15_000;

  const [intervalRefreshCount, setIntervalRefreshCount] = useState(0);
  const [delay, setDelay] = useState<number | undefined>(refreshIntervalMs);
  const { maximumRefreshCount } = useMaximumRefreshCount(slot, intervalRefreshCount);

  // Reset counters when slot change
  useEffect(() => {
    setIntervalRefreshCount(0);
  }, [slot]);

  // Trigger refresh for every interval
  useInterval(() => {
    // Delay when it's being hovered
    if (isHovering) {
      setDelay(delayIntervalMs);
      return;
    }

    setIntervalRefreshCount(current => (current += 1));
    setDelay(refreshIntervalMs);
  }, delay);

  useEffect(() => {
    if (!isEnable) {
      setDelay(undefined);
      return;
    }

    if (intervalRefreshCount >= maximumRefreshCount) {
      // Stop when reach maximum refresh count
      setDelay(undefined);
      return;
    }

    // Stop when not visible
    if (!intersection?.isIntersecting) {
      setDelay(undefined);
      return;
    }

    setDelay(refreshIntervalMs);
  }, [
    isEnable,
    intersection?.isIntersecting,
    intervalRefreshCount,
    maximumRefreshCount,
    refreshIntervalMs,
    setDelay,
  ]);

  return { intervalRefreshCount };

  function useMaximumRefreshCount(slot: googletag.Slot | null, intervalRefreshCount: number) {
    const { advertisementConfig } = useAtomValue(appAtom);
    const isDesktop = useResponsive(theme.breakpoints.up("desktop"), true);
    const maximumRefreshCountPerCycle = isDesktop
      ? advertisementConfig.autoRefresh.desktopMaximumRefreshLimit
      : advertisementConfig.autoRefresh.mobileMaximumRefreshLimit;
    const [refreshCycle, setRefreshCycle] = useState(1);

    // Reset for slot change
    useEffect(() => {
      setRefreshCycle(1);
    }, [slot]);

    // Resume refresh for desktop after reaching maximum refresh count when user is active
    const isIdle = useIdle(advertisementConfig.autoRefresh.refreshIntervalMs / 2);
    useEffect(() => {
      if (!isDesktop) return;
      if (isIdle) return;

      if (intervalRefreshCount >= maximumRefreshCountPerCycle * refreshCycle) {
        setRefreshCycle(current => (current += 1));
      }
    }, [
      setRefreshCycle,
      isIdle,
      intervalRefreshCount,
      isDesktop,
      maximumRefreshCountPerCycle,
      refreshCycle,
    ]);

    return {
      maximumRefreshCount: maximumRefreshCountPerCycle * refreshCycle,
      resetMaximumRefreshCount: useCallback(() => setRefreshCycle(1), [setRefreshCycle]),
    };
  }
}

function useScrollBackRefresh(
  slot: googletag.Slot | null,
  intersection: IntersectionObserverEntry | null,
  isEnable: boolean,
) {
  const { advertisementConfig } = useAtomValue(appAtom);
  const minimalScrollAwayDurationMs = advertisementConfig.autoRefresh.minimalScrollAwayDurationMs;
  const [scrollBackRefreshCount, setScrollBackRefreshCount] = useState(0);
  const { lastScrollAwayDurationMs } = useLastScrollAwayDuration(intersection);

  useEffect(() => setScrollBackRefreshCount(0), [slot]);
  useEffect(() => {
    if (!isEnable) return;
    if (!lastScrollAwayDurationMs || lastScrollAwayDurationMs < minimalScrollAwayDurationMs) return;
    setScrollBackRefreshCount(current => (current += 1));
  }, [isEnable, setScrollBackRefreshCount, lastScrollAwayDurationMs, minimalScrollAwayDurationMs]);

  return {
    scrollBackRefreshCount,
  };

  function useLastScrollAwayDuration(intersection: IntersectionObserverEntry | null) {
    const [hasBeenIntersected, setHasBeenIntersected] = useState(false);
    const latestPerviousIntersection = useSyncedRef(usePrevious(intersection));
    const [lastScrollAwayDurationMs, setLastScrollAwayDurationMs] = useState<null | number>(null);

    useEffect(() => {
      if (!latestPerviousIntersection.current || !intersection) {
        setLastScrollAwayDurationMs(null);
        return;
      }

      // Track the ad slot has been intersected before starting counting scroll away duration
      if (intersection.isIntersecting && !hasBeenIntersected) {
        setHasBeenIntersected(true);
        return;
      }

      const isScrollingBack =
        !latestPerviousIntersection.current.isIntersecting && intersection.isIntersecting;

      setLastScrollAwayDurationMs(
        isScrollingBack ? intersection.time - latestPerviousIntersection.current.time : null,
      );
    }, [hasBeenIntersected, intersection, latestPerviousIntersection, setLastScrollAwayDurationMs]);

    return { lastScrollAwayDurationMs };
  }
}
